import { Button, Loader, Select, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { predictionApi } from "#/api/predictionApi";
import ActionButton from "#/components/buttons/ActionButton";
import DetailPageLayout from "#/components/ui/DetailPageLayout";
import { EmptyState } from "#/components/ui/EmptyState";
import { useAuth } from "#/hook/useAuth";
import { useClient } from "#/hook/useClient";
import { usePrvTranslation } from "#/pages/ClientDetail/components/prv/usePrvTranslation";
import { UserRole } from "#/types/user";
import { addDays, formatDate, formatDateTime, startOfIsoWeek } from "#/utils/dateTimeUtils";
import NotPredictableModal from "./components/NotPredictableModal";
import PredictionWeekCalendar from "./components/PredictionWeekCalendar";

/**
 * Predicted Deliveries (Fleet & Logistics). Header with "as of", the routing
 * state and the not-predictable link; filters on the left, Today / week label
 * / previous and next week on the right; then a Monday-to-Sunday week
 * calendar with one block per shipment. Past weeks show what was planned,
 * muted, with the outcome. A block opens the shipment. A TMA admin reaches it
 * with `?clientId=`.
 */
const PredictedDeliveriesPage = () => {
  const t = usePrvTranslation();
  const { user } = useAuth();
  const { client } = useClient();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const adminClientId =
    user?.role === UserRole.ADMIN ? Number(searchParams.get("clientId")) || undefined : undefined;

  const [siteFilter, setSiteFilter] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [notPredictableOpened, { open: openNotPredictable, close: closeNotPredictable }] =
    useDisclosure(false);
  const now = useMemo(() => new Date(), []);
  const currentWeekStart = useMemo(() => startOfIsoWeek(now), [now]);
  const [weekStart, setWeekStart] = useState<Date>(currentWeekStart);
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);
  const onCurrentWeek = weekStart.getTime() === currentWeekStart.getTime();
  const clientQuery = adminClientId ? `?clientId=${adminClientId}` : "";
  const openShipment = (shipmentId: number) =>
    void navigate(`/fleet/predicted-deliveries/shipments/${shipmentId}${clientQuery}`);

  const {
    data: board,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["prediction-board", adminClientId ?? "own", weekStart.getTime()],
    queryFn: async () =>
      (await predictionApi.getBoard(adminClientId, { from: weekStart, to: weekEnd })).data,
    staleTime: 60_000,
  });

  // Site = the Site a shipment leaves from (its live plan's origin).
  const siteOptions = useMemo(() => {
    const sites = new Map<number, string>();
    for (const card of board?.shipments ?? []) {
      const origin = card.plan?.origin;
      if (origin) sites.set(origin.siteId, origin.name);
    }
    return [...sites.entries()]
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([id, name]) => ({ value: String(id), label: name }));
  }, [board]);

  const filtered = useMemo(() => {
    if (!board) return [];
    return board.shipments.filter((card) => {
      if (siteFilter && String(card.plan?.origin?.siteId ?? "") !== siteFilter) return false;
      if (
        categoryFilter &&
        !card.predictions.some((prediction) =>
          prediction.lines.some((line) => String(line.productId) === categoryFilter)
        )
      ) {
        return false;
      }
      return true;
    });
  }, [board, siteFilter, categoryFilter]);

  if (user?.role === UserRole.ADMIN && !adminClientId) {
    return <EmptyState title={t("predictedDeliveries.adminNeedsClient")} />;
  }

  const body = (
    <div className="flex h-full w-full flex-col gap-4 overflow-hidden">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          {!adminClientId && <Title order={2}>{t("predictedDeliveries.title")}</Title>}
          <span className="text-xs text-gray-500">
            {board?.asOf
              ? t("predictedDeliveries.asOf", { date: formatDateTime(board.asOf, client) })
              : t("predictedDeliveries.neverComputed")}
            {board && !board.routing.available && (
              <>
                {" · "}
                <span className="text-orange-700">
                  {t(`predictedDeliveries.routing.${board.routing.reason}`)}
                </span>
              </>
            )}
            {board && board.notPredictable.length > 0 && (
              <>
                {" · "}
                <button
                  type="button"
                  className="text-blue-700 underline-offset-2 hover:underline"
                  onClick={openNotPredictable}
                >
                  {t("predictedDeliveries.notPredictable.link", {
                    count: board.notPredictable.length,
                  })}
                </button>
              </>
            )}
          </span>
        </div>
        {/* Filters, a divider, then the week controls: one row with the title. */}
        {board && (
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap gap-3">
              <Select
                placeholder={t("predictedDeliveries.filters.site")}
                data={siteOptions}
                value={siteFilter}
                onChange={setSiteFilter}
                clearable
                size="xs"
                className="w-56"
              />
              <Select
                placeholder={t("predictedDeliveries.filters.category")}
                data={board.categories.map((category) => ({
                  value: String(category.id),
                  label: category.name,
                }))}
                value={categoryFilter}
                onChange={setCategoryFilter}
                clearable
                searchable
                size="xs"
                className="w-56"
              />
            </div>
            <span className="h-6 w-px bg-gray-300" aria-hidden="true" />
            <div className="flex items-center gap-2">
              <Button
                variant="default"
                size="xs"
                disabled={onCurrentWeek}
                onClick={() => setWeekStart(currentWeekStart)}
              >
                {t("predictedDeliveries.calendar.today")}
              </Button>
              <span className="min-w-[13rem] text-center text-sm font-medium text-gray-800 tabular-nums">
                {t("predictedDeliveries.card.window", {
                  from: formatDate(weekStart, client),
                  to: formatDate(weekEnd, client),
                })}
              </span>
              <ActionButton
                icon={IconChevronLeft}
                variant="subtle"
                size="md"
                aria-label={t("predictedDeliveries.calendar.previousWeek")}
                onClick={() => setWeekStart((start) => addDays(start, -7))}
              />
              <ActionButton
                icon={IconChevronRight}
                variant="subtle"
                size="md"
                aria-label={t("predictedDeliveries.calendar.nextWeek")}
                onClick={() => setWeekStart((start) => addDays(start, 7))}
              />
            </div>
          </div>
        )}
      </div>

      {isLoading && (
        <div className="flex h-40 items-center justify-center">
          <Loader color="var(--client-color)" />
        </div>
      )}
      {isError && <EmptyState title={t("common.failedToProcess")} />}

      {board && (
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
          {board.asOf === null ? (
            <EmptyState
              title={t("predictedDeliveries.empty.title")}
              description={t("predictedDeliveries.empty.description")}
            />
          ) : (
            <PredictionWeekCalendar
              board={board}
              cards={filtered}
              weekStart={weekStart}
              now={now}
              onOpen={openShipment}
            />
          )}
        </div>
      )}

      {board && (
        <>
          <NotPredictableModal
            opened={notPredictableOpened}
            onClose={closeNotPredictable}
            rows={board.notPredictable}
            settings={board.settings}
          />
        </>
      )}
    </div>
  );

  // A TMA admin arrives from a client's Logistics Prediction Engine tab: same
  // detail layout as Client Details, with the back arrow. Client users see it
  // as a normal menu page.
  if (adminClientId) {
    return <DetailPageLayout title={t("predictedDeliveries.title")}>{body}</DetailPageLayout>;
  }
  return body;
};

export default PredictedDeliveriesPage;
